/**
 * Media CMS Adapter
 * -----------------
 * Pure functions that map the raw `/api/media?lang=<lang>` response
 * into the exact data shape the public Media Center components already
 * consume via `useMediaContent()`. Zero React, zero side effects.
 *
 * Contract
 *   • Input: whatever `/api/media` returns (object or null/undefined).
 *   • Output: an object whose keys mirror the hardcoded constants
 *     exported from `lib/mediaData.js` and used by `useMediaContent()`.
 *   • Any section that is missing, malformed, or empty on the API
 *     returns `undefined`. The caller then substitutes the hardcoded
 *     fallback for that specific key — so a partial CMS payload never
 *     blanks the page.
 *
 * Ordering / visibility
 *   • The API already emits `visible: true` items only (composed by
 *     `compose_public_payload`), but we defensively re-filter and
 *     re-sort so a schema regression can't leak drafts or shuffle.
 *
 * Design notes
 *   • Hero / biography one-liners / feature-story eyebrow are NOT
 *     returned by the current hook — the on-page components read those
 *     via `ui.sections.*` (UI copy owned by `UI_EN`/`UI_AR`), so we
 *     leave them out of the adapter to preserve existing behaviour.
 *   • Static hero portraits and other file-system PHOTOS constants
 *     remain hardcoded — the API does not yet emit per-portrait
 *     variants and the Media Center design depends on them existing.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isNonEmptyArray(v) {
  return Array.isArray(v) && v.length > 0;
}

function nonBlank(v) {
  return typeof v === "string" && v.trim().length > 0;
}

/** Filter items by `visible !== false` and sort by `order` if present.
 *  Returns:
 *    • `undefined` if `list` is not an Array (section entirely absent
 *      from the CMS payload — health check will fall through to the
 *      hardcoded fallback for this section).
 *    • `[]` if the array is present-but-empty or all items are hidden
 *      (intentional admin deletion — the frontend must respect this
 *      and NOT resurrect fallback items via array merging).
 *    • `[items]` when items survive filtering.
 */
function sanitiseList(list) {
  if (!Array.isArray(list)) return undefined;
  const clean = list.filter((it) => it && it.visible !== false);
  clean.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return clean;
}

// ---------------------------------------------------------------------------
// Per-section adapters — each returns `undefined` on a broken/absent section
// so `useMediaContent` can transparently fall back to the hardcoded const.
// ---------------------------------------------------------------------------
export function adaptTimeline(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean.map((t) => ({
    year:  nonBlank(t.year)  ? t.year  : "—",
    title: nonBlank(t.title) ? t.title : "",
    copy:  nonBlank(t.copy)  ? t.copy  : "",
  }));
}

export function adaptHeroStats(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean.map((s) => ({
    value: nonBlank(s.value) ? s.value : "",
    label: nonBlank(s.label) ? s.label : "",
  }));
}

/**
 * Section 04 — "By the Numbers". Dedicated CMS section (`numbers`)
 * that is independent of the Hero Stats collection. Each row exposes
 * `{value, label, copy}`.
 */
export function adaptNumbers(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean.map((n) => ({
    value: nonBlank(n.value) ? n.value : "",
    label: nonBlank(n.label) ? n.label : "",
    copy:  nonBlank(n.copy)  ? n.copy  : "",
  }));
}

export function adaptQuickFacts(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean.map((f) => ({
    label: nonBlank(f.label) ? f.label : "",
    value: nonBlank(f.value) ? f.value : "",
  }));
}

export function adaptBioParagraphs(biography) {
  if (!biography || typeof biography !== "object") return undefined;
  const p = biography.main_paragraphs;
  if (!isNonEmptyArray(p)) return undefined;
  const out = p.filter(nonBlank);
  return out.length ? out : undefined;
}

export function adaptFeatureStory(feature) {
  if (!feature || typeof feature !== "object") return { headline: undefined, paragraphs: undefined };
  const headline = nonBlank(feature.headline) ? feature.headline : undefined;
  const paras = isNonEmptyArray(feature.paragraphs)
    ? feature.paragraphs.filter(nonBlank)
    : [];
  return {
    headline,
    paragraphs: paras.length ? paras : undefined,
  };
}

export function adaptArtists(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  const names = clean.map((a) => a.name).filter(nonBlank);
  return names;
}

export function adaptGenres(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  const names = clean.map((g) => g.name).filter(nonBlank);
  return names;
}

export function adaptRecognition(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean.map((r) => ({
    title: nonBlank(r.title) ? r.title : "",
    copy:  nonBlank(r.copy)  ? r.copy  : "",
  }));
}

export function adaptPressArticles(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean
    .filter((a) => nonBlank(a.url))
    .map((a) => ({
      publication: nonBlank(a.publication) ? a.publication : "",
      headline:    nonBlank(a.headline)    ? a.headline    : "",
      url:         a.url,
      date:        nonBlank(a.date) ? a.date : "",
    }));
}

/**
 * Platform features — feeds Section 09 (DJSPYOfficial.com). The public
 * page rendered these as hardcoded UI strings previously; wiring the
 * CMS list through here ensures admin deletions (e.g. "Membership")
 * are honored instead of being resurrected by the fallback array.
 */
export function adaptPlatformFeatures(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean
    .map((f) => (nonBlank(f.name) ? f.name : ""))
    .filter(nonBlank);
}

/**
 * Downloads — API items expose `item_id`; the hardcoded shape uses
 * `key`. Preserve `available` gating and drop items without an href.
 */
export function adaptDownloads(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean
    .filter((d) => nonBlank(d.href))
    .map((d) => ({
      key:       d.item_id || d.key || "",
      label:     nonBlank(d.label) ? d.label : "",
      available: d.available !== false,
      href:      d.href,
      filename:  nonBlank(d.filename) ? d.filename : "",
      note:      nonBlank(d.note) ? d.note : "",
      // en_only defaults to false — the CMS is providing localised
      // labels, so a download is considered translated once its AR
      // block has non-empty label + note (which the composer merged
      // via _merge_localised). The AR fallback dataset still needs
      // this key present or the AR downloads grid renders nothing.
      en_only:   !!d.en_only,
    }));
}

/**
 * Verified Presence — mapped into the `PLATFORMS` shape currently
 * consumed by MediaCenter.jsx: {name, handle, url}. Every visible
 * verified_presence item lands here regardless of `show_in_bookings`.
 */
export function adaptPlatforms(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean
    .filter((p) => nonBlank(p.url))
    .map((p) => ({
      name:   nonBlank(p.platform_name) ? p.platform_name : (p.name || ""),
      handle: nonBlank(p.handle) ? p.handle : "",
      url:    p.url,
    }));
}

/**
 * Direct Channels — subset of verified_presence flagged with
 * `show_in_bookings === true`. Refactored out of the hardcoded
 * MediaCenter.jsx block so editing a canonical contact (e.g. adding
 * Snapchat, changing an Instagram handle) instantly propagates to
 * both Verified Presence AND the booking form's Direct Channels
 * list. Legacy items lacking `show_in_bookings` default to true so
 * pre-migration data preserves current visibility.
 */
export function adaptBookingChannels(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  return clean
    .filter((p) => nonBlank(p.url))
    .filter((p) => p.show_in_bookings === undefined ? true : !!p.show_in_bookings)
    .map((p) => ({
      name:   nonBlank(p.platform_name) ? p.platform_name : (p.name || ""),
      handle: nonBlank(p.handle) ? p.handle : "",
      url:    p.url,
    }));
}

/**
 * Gallery — the hardcoded shape is a list of categories, each with an
 * `items[]` array of {src, alt}. The API emits a flat list where each
 * item carries its own `category`. We regroup, preserving order.
 */
export function adaptGallery(list) {
  const clean = sanitiseList(list);
  if (clean === undefined) return undefined;
  const byCat = new Map();
  const catOrder = [];
  for (const g of clean) {
    if (!nonBlank(g.src)) continue;
    const cat = nonBlank(g.category) ? g.category : "Uncategorised";
    if (!byCat.has(cat)) {
      byCat.set(cat, []);
      catOrder.push(cat);
    }
    byCat.get(cat).push({
      src: g.src,
      alt: nonBlank(g.alt) ? g.alt : "",
    });
  }
  return catOrder.map((category) => ({
    category,
    items: byCat.get(category),
  }));
}

/** Booking contact points — shape matches `BOOKING` const. */
export function adaptBooking(bs) {
  if (!bs || typeof bs !== "object") return undefined;
  return {
    email:     nonBlank(bs.email)     ? bs.email     : "",
    telegram:  nonBlank(bs.telegram)  ? bs.telegram  : "",
    instagram: nonBlank(bs.instagram) ? bs.instagram : "",
    tiktok:    nonBlank(bs.tiktok)    ? bs.tiktok    : "",
    website:   nonBlank(bs.website)   ? bs.website   : "",
  };
}

// ---------------------------------------------------------------------------
// Top-level adapter
// ---------------------------------------------------------------------------
/**
 * Adapt a full `/api/media` payload into the useMediaContent-facing
 * shape. Returns an object with the same keys as the hardcoded
 * constants; unmapped/broken sections are omitted so the caller can
 * fall through to the hardcoded default per-key.
 */
export function adaptCmsPayload(payload) {
  if (!payload || typeof payload !== "object") return null;

  const disc = (payload.disclaimers && typeof payload.disclaimers === "object")
    ? payload.disclaimers
    : {};

  const featureStory = adaptFeatureStory(payload.feature_story);

  const out = {
    TIMELINE:              adaptTimeline(payload.timeline),
    TIMELINE_DISCLAIMER:   nonBlank(disc.timeline)  ? disc.timeline  : undefined,
    ALONGSIDE:             adaptArtists(payload.artists),
    ALONGSIDE_DISCLAIMER:  nonBlank(disc.alongside) ? disc.alongside : undefined,
    GENRES:                adaptGenres(payload.genres),
    RECOGNITION:           adaptRecognition(payload.recognition),
    PRESS_ARTICLES:        adaptPressArticles(payload.press_articles),
    DOWNLOADS:             adaptDownloads(payload.downloads),
    DOWNLOAD_USAGE_NOTICE: nonBlank(disc.download_usage) ? disc.download_usage : undefined,
    HERO_STATS:            adaptHeroStats(payload.stats),
    NUMBERS:               adaptNumbers(payload.numbers),
    STATS_DISCLAIMER:      nonBlank(disc.stats) ? disc.stats : undefined,
    QUICK_FACTS:           adaptQuickFacts(payload.quick_facts),
    BIO_PARAGRAPHS:        adaptBioParagraphs(payload.biography),
    VISION_HEADLINE:       featureStory.headline,
    VISION_PARAGRAPHS:     featureStory.paragraphs,
    GALLERY:               adaptGallery(payload.gallery),
    PLATFORMS:             adaptPlatforms(payload.verified_presence),
    BOOKING_CHANNELS:      adaptBookingChannels(payload.verified_presence),
    PLATFORM_FEATURES:     adaptPlatformFeatures(payload.platform_features),
    BOOKING:               adaptBooking(payload.booking_settings),
  };

  // Strip undefined keys so the merge upstream is "override only".
  const cleaned = {};
  for (const [k, v] of Object.entries(out)) {
    if (v !== undefined) cleaned[k] = v;
  }
  return cleaned;
}

/**
 * Minimal shape sanity check — used before we accept the CMS payload
 * to drive the page. Anything critical missing → skip CMS, use
 * fallback wholesale. This is defence-in-depth on top of the
 * per-section undefined-guards.
 */
export function isPayloadHealthy(payload) {
  if (!payload || typeof payload !== "object") return false;
  // A healthy payload must at minimum carry biography paragraphs and
  // the hero stats — everything else can safely degrade to fallback.
  const bio = payload.biography;
  const hasBio = bio && Array.isArray(bio.main_paragraphs) && bio.main_paragraphs.length > 0;
  const hasStats = Array.isArray(payload.stats) && payload.stats.length > 0;
  return Boolean(hasBio && hasStats);
}
